import { ArrowLeft, Heart } from "lucide-react";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { useNavigate } from "react-router-dom";
import { useHomeDataForDate } from "../../hooks/useHomeDataForDate";
import { FAT_QUALITY_SCORE_BANDS } from "../../utils/healthInsightScoreBands";
import { HealthInsightScoreBandsList } from "../../components/HealthInsights/HealthInsightScoreBandsList";
import { formatHealthInsightScore, hasHealthInsightScore } from "../../utils/healthInsightsMetrics";

const FatQualityDetail = () => {
  const navigate = useNavigate();

  // Fetch data for the selected date
  const {
    data: homeData,
    isLoading: loading,
    formattedSelectedDate,
  } = useHomeDataForDate();

  // Get fat quality data from API
  const insightData = homeData?.["Health Insights"]?.bad_fat_ratio_daily;
  const fatQualityStatus = insightData?.label ?? "Loading...";
  const fatQualityScore = insightData?.score;
  const fatQualityScoreText = formatHealthInsightScore(fatQualityScore);
  const hasScore = hasHealthInsightScore(fatQualityScore);

  // Get actual gram values from Today's Nutrition
  const totalFat = homeData?.["Today's Nutrition"]?.["Total Fats"] || 0;
  const saturatedFat = homeData?.["Today's Nutrition"]?.["Total Saturated Fat"] || 0;
  const transFat = homeData?.["Today's Nutrition"]?.["Total Trans Fat"] || 0;



  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-center">
          <div className="mb-4 h-12 w-12 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          <p className="text-footnote text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-16">
      <div className="mx-auto max-w-md space-y-4 p-4">
        {/* Header */}
        <div className="flex items-center justify-between pt-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
            className="rounded-xl"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-title2 font-extrabold text-foreground">
            {formattedSelectedDate} Fat Quality
          </h1>
          <div className="h-10 w-10" />
        </div>

        {/* Today's Stats */}
        <div className="relative overflow-hidden rounded-2xl border border-border/60 bg-white p-5 shadow-lg dark:border-blue-800/30 dark:bg-card">
          <div className="absolute left-0 top-0 h-full w-1.5 bg-gradient-to-b from-blue-400 to-sky-500" />
          
          <div className="mb-6 flex items-center gap-4">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900/40">
              <Heart className="h-6 w-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <p className="text-subhead font-semibold text-muted-foreground">Today's Fat Quality</p>
                <span className={`inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-2xs font-bold uppercase tracking-wider ${
                  !hasScore
                    ? "bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400"
                    : fatQualityStatus === "Good" || fatQualityStatus === "Excellent"
                    ? "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400"
                    : fatQualityStatus === "Needs Improvement"
                    ? "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400"
                    : "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400"
                }`}>
                  {!hasScore ? "No Data" : fatQualityStatus}
                </span>
              </div>
              <div className="mt-1 flex items-baseline gap-2">
                <span className="text-4xl font-extrabold tracking-tight text-foreground">
                  {fatQualityScoreText ?? "—"}
                </span>
              </div>
            </div>
          </div>

          {/* Fat Breakdown */}
          <div className="rounded-xl bg-gray-50 p-4 dark:bg-gray-800/40">
            <div className="flex justify-between md:justify-around">
              <div className="text-center">
                <div className="text-title2 font-bold text-foreground">
                  {Math.round(totalFat)}g
                </div>
                <div className="text-caption1 font-medium text-muted-foreground">Total Fat</div>
              </div>
              <div className="text-center">
                <div className="text-title2 font-bold text-purple-600 dark:text-purple-400">
                  {Math.round(saturatedFat)}g
                </div>
                <div className="text-caption1 font-medium text-muted-foreground">Saturated</div>
              </div>
              <div className="text-center">
                <div className="text-title2 font-bold text-red-600 dark:text-red-400">
                  {Math.round(transFat)}g
                </div>
                <div className="text-caption1 font-medium text-muted-foreground">Trans Fat</div>
              </div>
            </div>
          </div>
        </div>

        {/* What is Fat Quality */}
        <Card className="rounded-2xl border-0 p-5 shadow-sm">
          <h3 className="mb-3 text-title3 font-bold text-foreground">
            What is Fat Quality?
          </h3>
          <p className="text-callout leading-relaxed text-muted-foreground">
            Fat Quality measures the ratio of healthy (unsaturated) fats to
            unhealthy (saturated and trans) fats in your diet. It is calculated
            using your total fat, saturated fat, and trans fat intake. A higher
            score indicates better fat quality, which supports heart health and
            reduces inflammation.
          </p>
          <div className="mt-4">
            <p className="mb-2 text-subhead font-semibold text-foreground">Score Ranges</p>
            <HealthInsightScoreBandsList bands={FAT_QUALITY_SCORE_BANDS} />
          </div>
        </Card>

        {/* Improvement Tips */}
        <Card className="rounded-2xl border-0 p-5 shadow-sm">
          <h3 className="mb-3 text-title3 font-bold text-foreground">
            Tips to Improve Fat Quality
          </h3>
          <ul className="space-y-2 text-callout text-muted-foreground">
            <li className="flex items-start gap-2">
              <span className="mt-0.5 text-chart-1">•</span>
              <span>Replace butter with olive oil or avocado oil</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="mt-0.5 text-chart-1">•</span>
              <span>Avoid processed and fried foods high in trans fats</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="mt-0.5 text-chart-1">•</span>
              <span>Choose lean proteins and remove visible fat from meats</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="mt-0.5 text-chart-1">•</span>
              <span>Add nuts, seeds, and fatty fish to your diet</span>
            </li>
          </ul>
        </Card>
      </div>
    </div>
  );
};

export default FatQualityDetail;
